import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { formatPercentLabel, numberToDollars } from '../utils'
import { buildArcPath } from './RetirementIncomeDonut/arcGeometry'
import { PLAN_BLUE } from './RetirementIncomeDonut'

const FONT_FAMILY = "'Open Sans', 'Helvetica Neue', sans-serif"

const RADIUS = 50
const THICKNESS = 7
const PADDING = 2

const AMOUNT_OFFSET_Y = -12
const AMOUNT_FONT_SIZE = 18
const LABEL_OFFSET_Y = 4
const LABEL_FONT_SIZE = 12

// Estimate the space inside the ring at the top of the amount text.
const AMOUNT_CAP_Y = AMOUNT_OFFSET_Y - AMOUNT_FONT_SIZE * 0.72
const AMOUNT_FIT_WIDTH = 2 * Math.sqrt((RADIUS - THICKNESS) ** 2 - AMOUNT_CAP_Y ** 2)
const AMOUNT_MAX_WIDTH = AMOUNT_FIT_WIDTH - 4

const VIEWBOX_MIN = -(RADIUS + PADDING)
const WIDTH = 2 * (RADIUS + PADDING)
const HEIGHT = RADIUS + PADDING + LABEL_OFFSET_Y + LABEL_FONT_SIZE + PADDING

const Chart = styled.svg`
  display: block;
  width: 100%;
  max-width: ${WIDTH}px;
  height: auto;
`

const arcTo = toPercent =>
  buildArcPath({
    innerRadius: RADIUS - THICKNESS,
    outerRadius: RADIUS,
    toPercent,
    cornerRadius: THICKNESS / 2,
  })

const TRACK_PATH = arcTo(100)

const MiniIncomeGauge = ({ percent, amount, label = 'monthly', className }) => {
  const theme = useTheme()
  const track = theme.ternaryColor
  const plan = theme.donutPlanColor || PLAN_BLUE
  const valueColor = theme.primaryColor

  const amountText = amount == null ? null : `$${numberToDollars(amount, true)}`
  const amountOverflows =
    amountText != null && amountText.length * AMOUNT_FONT_SIZE * 0.6 > AMOUNT_FIT_WIDTH

  const ariaLabel =
    percent == null
      ? 'No goal selected'
      : `${formatPercentLabel(percent)}% of current income${
          amountText == null ? '' : `, ${amountText} ${label}`
        }`

  return (
    <Chart
      viewBox={`${VIEWBOX_MIN} ${VIEWBOX_MIN} ${WIDTH} ${HEIGHT}`}
      role='img'
      aria-label={ariaLabel}
      className={className}>
      <path d={TRACK_PATH} fill={track} />
      {percent != null && <path d={arcTo(percent)} fill={plan} />}

      {amountText != null && (
        <text textAnchor='middle' fontFamily={FONT_FAMILY} fill={valueColor}>
          <tspan
            x={0}
            y={AMOUNT_OFFSET_Y}
            fontSize={AMOUNT_FONT_SIZE}
            fontWeight={700}
            textLength={amountOverflows ? AMOUNT_MAX_WIDTH : undefined}
            lengthAdjust='spacingAndGlyphs'>
            {amountText}
          </tspan>
          <tspan x={0} y={LABEL_OFFSET_Y} fontSize={LABEL_FONT_SIZE} fontWeight={400}>
            {label}
          </tspan>
        </text>
      )}
    </Chart>
  )
}

export default MiniIncomeGauge
